import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getPurchaseReturns, createPurchaseReturn, getAccounts, getSuppliers, getProducts, generateReturnNumber } from '@/services/firestore'
import { useAuth } from '@/contexts/AuthContext'
import { RotateCw, Plus, X, Save, RefreshCw } from 'lucide-react'

export const Route = createFileRoute('/admin/purchase-returns')({ component: PurchaseReturnsPage })

function PurchaseReturnsPage() {
    const { user } = useAuth()
    const [returns, setReturns] = useState<any[]>([])
    const [suppliers, setSuppliers] = useState<any[]>([])
    const [products, setProducts] = useState<any[]>([])
    const [accounts, setAccounts] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [showModal, setShowModal] = useState(false)
    const [saving, setSaving] = useState(false)
    const [selectedSupplier, setSelectedSupplier] = useState('')
    const [items, setItems] = useState<any[]>([{ productId: '', productName: '', quantity: 1, unitPrice: 0, totalPrice: 0 }])
    const [reason, setReason] = useState('')

    useEffect(() => { load() }, [])
    const load = async () => {
        setLoading(true)
        const [r, s, p, a] = await Promise.all([getPurchaseReturns(), getSuppliers(), getProducts(), getAccounts()])
        setReturns(r); setSuppliers(s); setProducts(p); setAccounts(a)
        setLoading(false)
    }

    const addItem = () => setItems([...items, { productId: '', productName: '', quantity: 1, unitPrice: 0, totalPrice: 0 }])
    const removeItem = (idx: number) => setItems(items.filter((_, i) => i !== idx))
    const updateItem = (idx: number, field: string, value: any) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== idx) return item
            const updated = { ...item, [field]: value }
            if (field === 'productId') {
                const product = products.find(p => p.id === value)
                if (product) { updated.productName = product.name; updated.unitPrice = product.costPrice || product.price }
            }
            updated.totalPrice = updated.quantity * updated.unitPrice
            return updated
        }))
    }

    const handleCreate = async () => {
        setSaving(true)
        try {
            const validItems = items.filter(i => i.productId && i.quantity > 0)
            const totalAmount = validItems.reduce((s, i) => s + i.totalPrice, 0)
            const supplier = suppliers.find(s => s.id === selectedSupplier)
            const purchaseReturnsAcc = accounts.find(a => a.code === '5002')
            const payableAcc = accounts.find(a => a.code === '2001')

            await createPurchaseReturn({
                returnNumber: generateReturnNumber('PR'), date: new Date().toISOString().split('T')[0],
                supplierId: selectedSupplier, supplierName: supplier?.name || '', items: validItems,
                totalAmount, reason, createdBy: user?.email || 'admin',
            }, { purchaseReturnsAccId: purchaseReturnsAcc?.id || '', payableAccId: payableAcc?.id || '' })
            await load()
            setShowModal(false); setSelectedSupplier(''); setItems([{ productId: '', productName: '', quantity: 1, unitPrice: 0, totalPrice: 0 }]); setReason('')
        } finally { setSaving(false) }
    }

    const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2 })

    return (
        <div className="p-4 pt-14 lg:pt-6 lg:p-6 bg-[#f0f5ff] min-h-screen">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2"><RotateCw className="w-6 h-6 text-blue-500" />Purchase Returns</h1>
                    <p className="text-xs text-slate-500 mt-0.5">Return goods to suppliers</p>
                </div>
                <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors">
                    <Plus className="w-4 h-4" />New Return
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : returns.length === 0 ? (
                <div className="text-center py-20 text-slate-400"><RotateCw className="w-12 h-12 mx-auto mb-3 opacity-30" /><p className="text-sm font-semibold">No purchase returns yet</p></div>
            ) : (
                <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-slate-50 border-b border-slate-100">
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Return #</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Date</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Supplier</th>
                            <th className="text-right px-4 py-3 text-xs font-bold text-slate-500 uppercase">Amount</th>
                            <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase">Reason</th>
                        </tr></thead>
                        <tbody>
                            {returns.map((r, i) => (
                                <tr key={r.id} className={`border-b border-slate-50 hover:bg-slate-50/50 ${i % 2 ? 'bg-slate-25' : ''}`}>
                                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-500">{r.returnNumber}</td>
                                    <td className="px-4 py-3 text-slate-600">{r.date}</td>
                                    <td className="px-4 py-3 font-semibold text-slate-700">{r.supplierName}</td>
                                    <td className="px-4 py-3 text-right font-mono font-bold text-red-600">{f(r.totalAmount)}</td>
                                    <td className="px-4 py-3 text-xs text-slate-500">{r.reason}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-slate-800">New Purchase Return</h2>
                            <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Supplier</label>
                                <select value={selectedSupplier} onChange={e => setSelectedSupplier(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50">
                                    <option value="">Select supplier...</option>
                                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>

                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead><tr className="bg-slate-50">
                                        <th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Qty</th>
                                        <th className="px-3 py-2 text-right">Unit Price</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2"></th>
                                    </tr></thead>
                                    <tbody>
                                        {items.map((item, idx) => (
                                            <tr key={idx} className="border-t border-slate-100">
                                                <td className="px-3 py-2"><select value={item.productId} onChange={e => updateItem(idx, 'productId', e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                                                    <option value="">Select...</option>{products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                </select></td>
                                                <td className="px-3 py-2"><input type="number" min={1} value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} className="w-16 px-2 py-1 border rounded text-right" /></td>
                                                <td className="px-3 py-2"><input type="number" value={item.unitPrice} onChange={e => updateItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)} className="w-24 px-2 py-1 border rounded text-right font-mono" /></td>
                                                <td className="px-3 py-2 text-right font-mono font-bold">{f(item.totalPrice)}</td>
                                                <td className="px-3 py-2"><button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button onClick={addItem} className="w-full py-2 text-xs text-blue-600 hover:bg-blue-50 font-semibold border-t border-slate-100">+ Add Item</button>
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-600 uppercase">Reason</label>
                                <input value={reason} onChange={e => setReason(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" placeholder="Reason for return" />
                            </div>

                            <div className="text-right text-lg font-bold text-red-600">
                                Total: PKR {f(items.reduce((s, i) => s + i.totalPrice, 0))}
                            </div>

                            <button onClick={handleCreate} disabled={saving || !selectedSupplier || !items.some(i => i.productId)}
                                className="w-full flex items-center justify-center gap-2 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">
                                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{saving ? 'Processing...' : 'Process Return'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
